<template>
  <page-template id="users">

    <div class="container-fluid page">
      <the-overlay :active="state === 'loading'">

        <users-filter @users-search="search"/>

        <data-table
            table-id="usersTable"
            :columns="headers"
            :items="users"
            :item-label="$t('adm.label.user', 2)"
            @item-click="editUser"
            :init-page-size="initialTableSize"
            ref="usersRef">

          <template v-slot:organization="{ item }">
            <a href="#" v-if="item.organization" @click.stop="showOrgDetails(item.organization)" class="link">
              <span class="d-inline-block">{{ getOrganization(item.organization)?.name }}</span>
              <stihl-icon
                  icon="status-error"
                  v-tooltip="$t('adm.label.inactiveOrg')"
                  v-if="getOrganization(item.organization)?.state === 'inactive'"/>
            </a>
          </template>

          <template v-slot:lastLogin="{ item }">
            <span v-if="!item.confirmed">
              <span v-text="$t('adm.label.unconfirmed')" class="d-inline-block"/> //
              <a href='#' @click.stop='resendMailToUser = item.id' class="d-inline-block">
                {{ $t('adm.button.resendMail') }}
              </a>
            </span>
            <span v-text="formatDate(item?.lastLogin)" v-else class="d-inline-block"/>
          </template>

          <template v-slot:syncFlag="{ item, index }">
            <a
                class="d-inline-block link"
                @click.stop="editUserFlag(index)"
                @keyup.space="editUserFlag(index)"
                aria-label="sync"
                v-if="item.confirmed">
              <sync-badge :flag="item.syncFlag" :read-only="!canEditSync(item)"/>
            </a>
          </template>

        </data-table>

      </the-overlay>
    </div>

    <confirmation-modal
        id="confirmMailSend"
        v-if="!!resendMailToUser"
        :title="$t('adm.page.resendMail.title')"
        :confirm="$t('adm.page.resendMail.confirmSend')"
        @confirm="resendConfirmationMail(resendMailToUser)"
        @cancel="resendMailToUser = ''"/>

  </page-template>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import useUserStore from '@/components/store/users.store';
import { mapActions, mapState, mapWritableState } from 'pinia';
import Backend, { BusinessType, QueryOptions, UserData, UserFilter } from '@/components/backend';
import { DisplayableOrg, isStihlEmployee, localeDateFormat } from '@/components/utils';
import { Logger, UserProfile } from 'oidc-client-ts';
import userManager from '@/components/oidc';
import { i18n } from '@/lang';
import PageTemplate from '@/components/layout/PageTemplate.vue';
import TheOverlay from '@/components/ui/TheOverlay.vue';
import SyncBadge from '@/components/ui/SyncBadge.vue';
import useAppStore from '@/components/store';
import ConfirmationModal from '@/components/ui/ConfirmationModal.vue';
import StihlIcon from '@/components/ui/StihlIcon.vue';
import UsersFilter from './UsersFilter.vue';
import DataTable, { TableColumns, TableRow } from '../../components/ui/DataTable.vue';

export default defineComponent({
  name: 'UsersPage',
  components: {
    StihlIcon,
    ConfirmationModal,
    SyncBadge,
    PageTemplate,
    UsersFilter,
    DataTable,
    TheOverlay,
  },
  data() {
    return {
      state: '',
      resendMailToUser: '', // contains the user-id if resend-modal is shown
    };
  },
  computed: {

    // inject state getters
    ...mapState(useAppStore, {
      myRole: 'getMyAdminRole',
      canEditUser: 'canEditUser',
    }),
    ...mapWritableState(useAppStore, {
      showOrganizationModal: 'showOrganizationModal',
      showUserSyncModal: 'showUserSyncModal',
      showTutorialsModal: 'showTutorialsModal',
      hideTutorialsOnStartup: 'hideTutorialsOnStartup',
      tutorialsShowed: 'tutorialsShowed',
    }),
    ...mapWritableState(useUserStore, {
      users: 'users',
      organizations: 'organizations',
    }),

    headers(): TableColumns {
      return {
        email: { label: this.$t('adm.label.email'), copyable: true, sortable: true },
        firstname: { label: this.$t('adm.label.firstname'), sortable: true },
        lastname: { label: this.$t('adm.label.lastname'), sortable: true },
        organization: { label: this.$t('adm.label.organization'), selfRender: true },
        crmNo: {
          label: this.$t('adm.label.crmNo'), sortable: true, copyable: true, decorator: this.crmNoDecorator,
        },
        lastLogin: { label: this.$t('adm.label.userHistory.LAST_LOGIN'), selfRender: true },
        adminLevel: { label: this.$t('adm.label.adminLevel'), sortable: true, decorator: this.adminLevelDecorator },
        // hide column for userAdmin
        ...this.myRole === 'userAdmin' ? {} : {
          syncFlag: {
            label: this.$t('adm.label.syncFlagShort'),
            selfRender: true,
            columnClass: 'w-7rem',
          },
        },
      };
    },

    initialTableSize(): number {
      if (window.innerHeight > 1890) return 35;
      return window.innerHeight > 1500 ? 25 : 15;
    },
  },
  methods: {

    ...mapActions(useUserStore, {
      getOrganization: 'getOrganization',
    }),

    editUser(i: number) {
      const user: UserData = this.users[i];
      Logger.debug('users', 'editUser', user);
      this.$router.push(`/${i18n.global.locale}/members/${user.id}`);
    },

    gotoUser(id: string) {
      // this.selectedOrganization = null; // close modal if open
      this.$router.push(`/${i18n.global.locale}/members/${id}`);
    },

    editUserFlag(index: number) {
      if (this.canEditSync(this.users[index])) {
        this.showUserSyncModal = this.users[index];
      } else {
        this.$toast.warning(this.$t('adm.error.NOT_ALLOWED'));
      }
    },

    canEditSync(user: UserData): boolean {
      // check my admin level allows it
      if (!this.canEditUser(user.adminLevel)) return false;
      // special checks for orgSuperAdmins
      if (this.myRole == 'orgSuperAdmin') {
        // WEBSSO-1050 cannot edit sync-flag for employees
        if (isStihlEmployee(user.email)) return false;
        // WEBSSO-1050 cannot edit sync-flag for Subsidiary/VU Accounts
        if (this.getOrganization(user.organization)?.businessType === BusinessType.Subsidiary) return false;
      }
      return true;
    },

    crmNoDecorator(item: TableRow): string {
      return this.getOrganization(item.organization)?.crmNo ?? '-';
    },

    formatDate(date: string) {
      return localeDateFormat(date, i18n.global.locale);
    },

    showOrgDetails(orgId: string) {
      Logger.debug('users', `show organization ${orgId}`);
      this.showOrganizationModal = orgId;
    },
    resendConfirmationMail(uid: string) {
      Backend.validateToken().then((api) => api.resendVerificationEmail(uid)
          .then(() => this.$toast.success(this.$t('adm.page.resendMail.infoSuccess')))
          .catch((e) => {
            if (e.response.status === 429) {
              this.$toast.error(this.$t('adm.error.throttling'));
            } else {
              this.$toast.error(this.$t('adm.error.generic'));
            }
          }));
    },

    adminLevelDecorator(item: TableRow) {
      return item?.adminLevel ? this.$t(`adm.label.adminRoles.${item.adminLevel}`) : '';
    },

    search(userFilter?: UserFilter) {
      Logger.debug('users', 'got search event');

      userManager.getValidUser().then((u) => {
        Logger.info('users', 'searchUsers by:', userFilter);

        this.users = [];
        this.state = 'loading';
        (this.$refs.usersRef as typeof DataTable).selectPage(1); // reset page
        // if (userFilter) this.$toast.clear(); // don't reset on page load

        const opt = {

          $select: 'id,firstname,lastname,email,unverifiedEmail,confirmed,organization,adminLevel,syncFlag,lastLogin,crmId,businessRoles',
          $expand: 'organization($select=id,name,crmNo,state,businessType)',
        } as QueryOptions;
        const filter = userFilter ?? {} as UserFilter;

        new Backend(u.access_token).findUsers(opt, filter).then((r) => {
          Logger.debug('users', 'user loaded');
          // this.users = r.data.items;
          this.users = r.data;
          this.organizations = r.extra?.organization ?? [];
          if (!r.complete) this.$toast.warning(this.$t('adm.error.limitExceeded'), { duration: 5000 });
          this.state = '';
        }).catch((e) => {
          // request canceled
          if (e.code === 'ERR_CANCELED') return;

          this.state = 'err';
          this.$toast.error(this.$t('adm.error.generic'));
        });
      });
    },

    organizationFromToken(p: UserProfile): DisplayableOrg[] {
      if (!p) return [];
      const orgId = p.dealer_org_id as string;
      const orgName = p.dealer_org_name as string;
      const crmNo = p.dealer_org_account_number as string;
      return [new DisplayableOrg({
        id: orgId,
        name: orgName,
        crmNo,
      })];
    },

  },

  beforeMount() {
    if (!this.tutorialsShowed) {
      Logger.info('app', 'loading preferences');
      Backend.validateToken().then((api) => api.getMyPreferences().then((resp) => {
        this.hideTutorialsOnStartup = resp.hideTutorials ?? false;
        if (!resp.hideTutorials) {
          this.showTutorialsModal = true;
          this.tutorialsShowed = true;
        }
      }));
    }
  },

});
</script>
